/**
 * Coach-level AI preferences persisted to localStorage. Read by callers
 * (and the Settings UI) to bias AI calls without touching every prompt
 * site. Two knobs:
 *
 *   - default effort: low / medium / high — applied when a callAI() site
 *     doesn't pass an explicit effort. Caller-supplied effort wins.
 *   - per-surface max_tokens override: integer, optional. Empty falls back
 *     to the prompt's intrinsic max_tokens. Useful when a coach wants
 *     longer narratives or to throttle expensive surfaces.
 *
 * No-op for any caller that doesn't read these — adding the controls
 * here doesn't change behaviour until a call site opts in.
 */
import type { SurfaceId } from '@4cm/core/aiSurfaces';

export type Effort = 'low' | 'medium' | 'high';

const EFFORT_KEY = '4cm_ai_effort';
const TOKENS_MAP_KEY = '4cm_ai_tokens_map';

const DEFAULT_EFFORT: Effort = 'medium';

export function getDefaultEffort(): Effort {
  try {
    const v = localStorage.getItem(EFFORT_KEY);
    if (v === 'low' || v === 'medium' || v === 'high') return v;
  } catch {
    /* localStorage may be unavailable (SSR, private mode) */
  }
  return DEFAULT_EFFORT;
}

export function setDefaultEffort(e: Effort): void {
  try {
    localStorage.setItem(EFFORT_KEY, e);
  } catch {
    /* swallow — best-effort persistence */
  }
}

function readTokensMap(): Record<string, number> {
  try {
    const raw = localStorage.getItem(TOKENS_MAP_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed as Record<string, number>;
  } catch {
    /* invalid JSON — fall through */
  }
  return {};
}

function writeTokensMap(map: Record<string, number>): void {
  try {
    localStorage.setItem(TOKENS_MAP_KEY, JSON.stringify(map));
  } catch {
    /* swallow */
  }
}

export function getMaxTokensFor(surface: SurfaceId): number | null {
  const map = readTokensMap();
  const v = map[surface];
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
}

export function setMaxTokensFor(surface: SurfaceId, tokens: number | null): void {
  const map = readTokensMap();
  if (tokens === null || !Number.isFinite(tokens) || tokens <= 0) {
    delete map[surface];
  } else {
    map[surface] = Math.round(tokens);
  }
  writeTokensMap(map);
}

export function getAllMaxTokens(): Record<string, number> {
  return readTokensMap();
}
